import React from 'react';
import { Box, Divider, FormControlLabel, Switch } from '@mui/material';
import { FormattedNumberTextField } from '../../common/FormattedNumberTextField';
import { GlobalSettingsNumberGrid } from '../../../pages/admin/GlobalSettingsNumberGrid';
import {
  chatToolLoopNumberFields,
  summaryNumberFields,
} from '../../../pages/admin/global-settings.config';
import { usePackages } from '../../../services/package.service';
import { SettingsSectionHeader } from './SettingsSectionHeader';
import type { SettingsSectionProps } from './settingsSectionTypes';

interface ChatSettingsSectionProps extends SettingsSectionProps {
  onPackageIterationChange: (packageCode: string, value: number | undefined) => void;
}

export const ChatSettingsSection: React.FC<ChatSettingsSectionProps> = ({
  config,
  onNumberChange,
  onBooleanChange,
  onPackageIterationChange,
}) => {
  const { data: packages } = usePackages();

  return (
    <>
      <SettingsSectionHeader
        title="Conversation summary"
        description="Controls the rolling summary layer for chat stream. The master switch also applies to Code Agent; its thresholds live under Code Agent > Runtime Settings."
      />
      <Box className="mb-5">
        <FormControlLabel
          control={
            <Switch
              checked={config.conversationSummaryEnabled}
              onChange={(event) =>
                onBooleanChange('conversationSummaryEnabled', event.target.checked)
              }
            />
          }
          label="Enable conversation summaries"
        />
      </Box>
      <GlobalSettingsNumberGrid
        config={config}
        fields={summaryNumberFields}
        onNumberChange={onNumberChange}
      />

      <Divider className="my-6" />

      <SettingsSectionHeader
        title="Chat tool loop"
        description="How many tool-call rounds one chat turn may run (search, files, diagrams) before the gateway stops. Leave a package blank to inherit the global value."
      />
      <Box className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <GlobalSettingsNumberGrid
          config={config}
          fields={chatToolLoopNumberFields}
          onNumberChange={onNumberChange}
        />
        {(packages ?? [])
          .filter((pkg) => pkg.isActive)
          .map((pkg) => (
            <FormattedNumberTextField
              key={pkg.code}
              label={`${pkg.name} (${pkg.code})`}
              helperText="Per-package cap; blank = global"
              value={config.chatStreamMaxToolIterationsByPackage[pkg.code.toUpperCase()]}
              onValueChange={(value) => onPackageIterationChange(pkg.code.toUpperCase(), value)}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
            />
          ))}
      </Box>
    </>
  );
};
